import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@medikei/ui';
import { Button } from '@medikei/ui';
import { Badge } from '@medikei/ui';
import {
  ArrowLeft,
  Save,
  Clock,
  Plus,
  Trash2,
  Calendar,
  Sun,
  Moon,
  AlertTriangle,
  X,
  Users,
  ArrowRight,
} from 'lucide-react';
import {
  useDoctorAvailability,
  useUpdateDoctorAvailability,
  useCreateDoctorAvailabilityException,
  useDeleteDoctorAvailabilityException,
} from '@/hooks/doctor/useDoctorAvailability';
import { useDoctorProfile } from '@/hooks/doctor/useDoctorProfile';
import { useAuth } from '@/hooks';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/utils/cn';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Domingo', short: 'DOM' },
  { value: 1, label: 'Segunda-feira', short: 'SEG' },
  { value: 2, label: 'Terça-feira', short: 'TER' },
  { value: 3, label: 'Quarta-feira', short: 'QUA' },
  { value: 4, label: 'Quinta-feira', short: 'QUI' },
  { value: 5, label: 'Sexta-feira', short: 'SEX' },
  { value: 6, label: 'Sábado', short: 'SÁB' },
];

interface TimeSlot {
  id: string;
  startTime: string;
  endTime: string;
  isActive: boolean;
}

interface AvailabilitySlot {
  dayOfWeek: number;
  slots: TimeSlot[];
}

interface Exception {
  id: string;
  date: string;
  isBlocked: boolean;
  startTime?: string;
  endTime?: string;
  reason?: string;
}

function DaySchedule({
  day,
  slots,
  onToggle,
  onAddSlot,
  onRemoveSlot,
  onUpdateSlot,
}: {
  day: typeof DAYS_OF_WEEK[0];
  slots: TimeSlot[];
  onToggle: (active: boolean) => void;
  onAddSlot: () => void;
  onRemoveSlot: (id: string) => void;
  onUpdateSlot: (id: string, field: 'startTime' | 'endTime', value: string) => void;
}) {
  const isActive = slots.length > 0 && slots.some((s) => s.isActive);

  return (
    <div className={`p-4 rounded-lg border ${isActive ? 'border-primary-500 bg-primary-500/5' : 'border-border'}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => onToggle(!isActive)}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              isActive ? 'bg-primary-500 text-white' : 'bg-muted text-muted-foreground'
            }`}
          >
            {isActive ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          <div>
            <p className="font-medium">{day.label}</p>
            <p className="text-sm text-muted-foreground">
              {isActive ? `${slots.length} horário(s)` : 'Indisponível'}
            </p>
          </div>
        </div>
        <Badge variant={isActive ? 'default' : 'outline'}>
          {isActive ? 'Ativo' : 'Desativado'}
        </Badge>
      </div>

      {isActive && (
        <div className="space-y-2">
          {slots.map((slot) => (
            <div key={slot.id} className="flex items-center gap-2">
              <input
                type="time"
                value={slot.startTime}
                onChange={(e) => onUpdateSlot(slot.id, 'startTime', e.target.value)}
                className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <span className="text-muted-foreground">às</span>
              <input
                type="time"
                value={slot.endTime}
                onChange={(e) => onUpdateSlot(slot.id, 'endTime', e.target.value)}
                className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                onClick={() => onRemoveSlot(slot.id)}
                className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button
            onClick={onAddSlot}
            className="flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 mt-2"
          >
            <Plus className="h-4 w-4" />
            Adicionar horário
          </button>
        </div>
      )}
    </div>
  );
}

function ExceptionsSection({
  exceptions,
  onAdd,
  onRemove,
}: {
  exceptions: Exception[];
  onAdd: () => void;
  onRemove: (id: string) => void;
}) {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="text-primary-500 h-5 w-5" />
          <h3 className="font-semibold">Exceções e Feriados</h3>
        </div>
        <Button variant="outline" size="sm" onClick={onAdd}>
          <Plus className="h-4 w-4 mr-2" />
          Adicionar Exceção
        </Button>
      </div>

      {exceptions.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>Nenhuma exceção cadastrada</p>
          <p className="text-sm">Adicione feriados ou dias de férias</p>
        </div>
      ) : (
        <div className="space-y-3">
          {exceptions.map((exception) => (
            <div
              key={exception.id}
              className={`flex items-center justify-between p-3 rounded-lg ${
                exception.isBlocked ? 'bg-red-500/10' : 'bg-yellow-500/10'
              }`}
            >
              <div className="flex items-center gap-3">
                {exception.isBlocked ? (
                  <X className="h-5 w-5 text-red-500" />
                ) : (
                  <Clock className="h-5 w-5 text-yellow-500" />
                )}
                <div>
                  <p className="font-medium">
                    {new Date(exception.date).toLocaleDateString('pt-BR', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                    })}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {exception.isBlocked
                      ? exception.startTime && exception.endTime
                        ? `Bloqueado (${exception.startTime} - ${exception.endTime})`
                        : 'Bloqueado (Dia inteiro)'
                      : `Horário Extra (${exception.startTime} - ${exception.endTime})`}
                    {exception.reason && ` - ${exception.reason}`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => onRemove(exception.id)}
                className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="text-sm text-muted-foreground mt-4">
        As exceções têm prioridade sobre os horários regulares.
      </p>
    </Card>
  );
}

function AddExceptionModal({
  onClose,
  onAdd,
}: {
  onClose: () => void;
  onAdd: (data: Omit<Exception, 'id'>) => void;
}) {
  const { toast } = useToast();
  const [date, setDate] = useState('');
  const [isBlocked, setIsBlocked] = useState(true);
  const [fullDay, setFullDay] = useState(true);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [reason, setReason] = useState('');

  const handleSubmit = () => {
    if (!date) {
      toast({ title: 'Selecione uma data para a exceção', variant: 'error' });
      return;
    }

    const needsTimeRange = !isBlocked || !fullDay;

    if (needsTimeRange) {
      if (!startTime || !endTime) {
        toast({ title: 'Informe o horário de início e fim', variant: 'error' });
        return;
      }
      if (startTime >= endTime) {
        toast({ title: 'O horário de início deve ser anterior ao de término', variant: 'error' });
        return;
      }
    }

    onAdd({
      date,
      isBlocked,
      startTime: needsTimeRange ? startTime : undefined,
      endTime: needsTimeRange ? endTime : undefined,
      reason: reason.trim() || undefined,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-in fade-in duration-150">
      <div className="bg-card text-card-foreground rounded-2xl p-6 w-full max-w-md border border-border shadow-2xl space-y-5">
        <h3 className="text-lg font-bold text-foreground">Adicionar Exceção / Feriado</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Data</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3.5 py-2.5 border border-border bg-background text-foreground rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">Tipo de Exceção</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setIsBlocked(true);
                  setFullDay(true);
                }}
                className={cn(
                  'flex-1 p-3 rounded-xl border transition-all text-center flex flex-col items-center justify-center gap-1',
                  isBlocked
                    ? 'border-destructive bg-destructive/10 text-destructive font-semibold shadow-xs'
                    : 'border-border bg-background text-muted-foreground hover:text-foreground'
                )}
              >
                <X className="h-5 w-5" />
                <span className="text-xs font-semibold">Bloquear</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsBlocked(false);
                  setFullDay(false);
                }}
                className={cn(
                  'flex-1 p-3 rounded-xl border transition-all text-center flex flex-col items-center justify-center gap-1',
                  !isBlocked
                    ? 'border-amber-500 bg-amber-500/10 text-amber-600 dark:text-amber-400 font-semibold shadow-xs'
                    : 'border-border bg-background text-muted-foreground hover:text-foreground'
                )}
              >
                <Clock className="h-5 w-5" />
                <span className="text-xs font-semibold">Horário Extra</span>
              </button>
            </div>
          </div>

          {isBlocked && (
            <div className="flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                id="fullDayCheckbox"
                checked={fullDay}
                onChange={(e) => setFullDay(e.target.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
              />
              <label htmlFor="fullDayCheckbox" className="text-sm text-foreground font-medium cursor-pointer">
                Bloquear o dia inteiro
              </label>
            </div>
          )}

          {(!isBlocked || !fullDay) && (
            <div className="flex gap-3 pt-1">
              <div className="flex-1">
                <label className="block text-xs font-semibold text-muted-foreground mb-1">Início</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-full px-3 py-2 border border-border bg-background text-foreground rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-semibold text-muted-foreground mb-1">Fim</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full px-3 py-2 border border-border bg-background text-foreground rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-foreground mb-1">Motivo (opcional)</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Férias, Feriado, Evento médico, Consulta particular..."
              className="w-full px-3.5 py-2.5 border border-border bg-background text-foreground placeholder:text-muted-foreground rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl border-border">
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit} className="flex-1 rounded-xl">
            Adicionar
          </Button>
        </div>
      </div>
    </div>
  );
}

export function DoctorAvailabilityPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const doctorId = user?.id || '';
  const [saving, setSaving] = useState(false);
  const [showExceptionModal, setShowExceptionModal] = useState(false);

  const { data: apiData } = useDoctorAvailability(doctorId || '');
  const { data: profile } = useDoctorProfile(doctorId || '');
  const updateAvailability = useUpdateDoctorAvailability(doctorId || '');
  const createException = useCreateDoctorAvailabilityException(doctorId || '');
  const deleteException = useDeleteDoctorAvailabilityException(doctorId || '');

  const [availability, setAvailability] = useState<AvailabilitySlot[]>(
    DAYS_OF_WEEK.map((day) => ({
      dayOfWeek: day.value,
      slots:
        day.value >= 1 && day.value <= 5
          ? [{ id: '1', startTime: '09:00', endTime: '18:00', isActive: true }]
          : [],
    }))
  );

  const [exceptions, setExceptions] = useState<Exception[]>([]);

  useEffect(() => {
    if (apiData) {
      setAvailability(
        DAYS_OF_WEEK.map((day) => {
          const apiSlots = apiData.slots
            .filter((s) => s.dayOfWeek === day.value)
            .map((s) => ({
              id: s.id,
              startTime: s.startTime,
              endTime: s.endTime,
              isActive: true,
            }));
          return { dayOfWeek: day.value, slots: apiSlots };
        })
      );
      setExceptions(
        apiData.exceptions.map((e) => ({
          id: e.id,
          date: new Date(e.date).toISOString().slice(0, 10),
          isBlocked: e.isBlocked,
          startTime: e.startTime ?? undefined,
          endTime: e.endTime ?? undefined,
          reason: e.reason ?? undefined,
        }))
      );
    }
  }, [apiData]);

  const handleToggleDay = (dayOfWeek: number, active: boolean) => {
    setAvailability((prev) =>
      prev.map((day) =>
        day.dayOfWeek === dayOfWeek
          ? {
              ...day,
              slots: active
                ? [{ id: Date.now().toString(), startTime: '09:00', endTime: '18:00', isActive: true }]
                : [],
            }
          : day
      )
    );
  };

  const handleAddSlot = (dayOfWeek: number) => {
    setAvailability((prev) =>
      prev.map((day) =>
        day.dayOfWeek === dayOfWeek
          ? {
              ...day,
              slots: [...day.slots, { id: Date.now().toString(), startTime: '09:00', endTime: '18:00', isActive: true }],
            }
          : day
      )
    );
  };

  const handleRemoveSlot = (dayOfWeek: number, slotId: string) => {
    setAvailability((prev) =>
      prev.map((day) =>
        day.dayOfWeek === dayOfWeek
          ? { ...day, slots: day.slots.filter((s) => s.id !== slotId) }
          : day
      )
    );
  };

  const handleUpdateSlot = (
    dayOfWeek: number,
    slotId: string,
    field: 'startTime' | 'endTime',
    value: string
  ) => {
    setAvailability((prev) =>
      prev.map((day) =>
        day.dayOfWeek === dayOfWeek
          ? {
              ...day,
              slots: day.slots.map((s) =>
                s.id === slotId ? { ...s, [field]: value } : s
              ),
            }
          : day
      )
    );
  };

  const handleAddException = async (data: Omit<Exception, 'id'>) => {
    if (!doctorId) return;
    try {
      const created = await createException.mutateAsync({
        date: new Date(data.date),
        isBlocked: data.isBlocked,
        startTime: data.startTime ?? null,
        endTime: data.endTime ?? null,
        reason: data.reason ?? null,
      });
      setExceptions((prev) => [
        ...prev,
        {
          id: created.id,
          date: new Date(created.date).toISOString().slice(0, 10),
          isBlocked: created.isBlocked,
          startTime: created.startTime ?? undefined,
          endTime: created.endTime ?? undefined,
          reason: created.reason ?? undefined,
        },
      ]);
    } catch (err) {
      console.error('Failed to persist exception', err);
    }
  };

  const handleRemoveException = async (id: string) => {
    if (!doctorId) return;
    const numericPrefix = /^\d+$/;
    const isLocalOnly = numericPrefix.test(id);
    setExceptions((prev) => prev.filter((e) => e.id !== id));
    if (isLocalOnly) return;
    try {
      await deleteException.mutateAsync(id);
    } catch (err) {
      console.error('Failed to delete exception', err);
    }
  };

  const handleSave = async () => {
    if (!doctorId || !updateAvailability) return;
    setSaving(true);
    try {
      await updateAvailability.mutateAsync({
        slots: availability.flatMap((day) =>
          day.slots.map((s) => ({
            dayOfWeek: day.dayOfWeek,
            startTime: s.startTime,
            endTime: s.endTime,
            isActive: s.isActive,
          }))
        ),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/medico/dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Disponibilidade</h1>
            <p className="text-muted-foreground mt-1">
              Configure seus horários de atendimento
            </p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            'Salvando...'
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Salvar
            </>
          )}
        </Button>
      </div>

      <div className="flex items-center gap-2 p-4 bg-yellow-500/10 rounded-lg">
        <AlertTriangle className="text-yellow-500 h-5 w-5 shrink-0" />
        <p className="text-sm">
          As alterações na disponibilidade podem afetar agendamentos futuros.
        </p>
      </div>

      {/* Card informativo de público e faixa etária atendida */}
      <Card className="p-4 sm:p-5 border-border bg-card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start sm:items-center gap-3.5">
            <div className="p-2.5 rounded-xl bg-primary-500/10 text-primary-600 dark:text-primary-400 shrink-0">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-foreground text-base">
                  Público e Faixa Etária Atendida
                </h3>
                <Badge
                  variant="outline"
                  className="text-xs bg-primary-500/10 text-primary-600 border-primary-500/30"
                >
                  {(profile?.minAge ?? 0) === 0 && (profile?.maxAge ?? 120) >= 120
                    ? 'Livre (Todas as Idades)'
                    : (profile?.maxAge ?? 120) <= 17
                    ? 'Pediatria'
                    : (profile?.minAge ?? 0) >= 18
                    ? 'Adultos e Idosos'
                    : 'Faixa Customizada'}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">
                Você está configurado para atender pacientes de {profile?.minAge ?? 0} a {profile?.maxAge ?? 120} anos.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/medico/perfil#publico-atendido')}
            className="shrink-0 self-start sm:self-auto"
          >
            Alterar Público
            <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary-500" />
            Horários da Semana
          </h2>
          {DAYS_OF_WEEK.map((day) => {
            const dayAvailability = availability.find((a) => a.dayOfWeek === day.value);
            return (
              <DaySchedule
                key={day.value}
                day={day}
                slots={dayAvailability?.slots || []}
                onToggle={(active) => handleToggleDay(day.value, active)}
                onAddSlot={() => handleAddSlot(day.value)}
                onRemoveSlot={(slotId) => handleRemoveSlot(day.value, slotId)}
                onUpdateSlot={(slotId, field, value) =>
                  handleUpdateSlot(day.value, slotId, field, value)
                }
              />
            );
          })}
        </div>

        <div>
          <ExceptionsSection
            exceptions={exceptions}
            onAdd={() => setShowExceptionModal(true)}
            onRemove={handleRemoveException}
          />
        </div>
      </div>

      {showExceptionModal && (
        <AddExceptionModal
          onClose={() => setShowExceptionModal(false)}
          onAdd={handleAddException}
        />
      )}
    </div>
  );
}
